import { TriviaList } from '@repo/types'
import { Container, Description, Heading, IconButton, Panel, Spinner, TrashIcon } from '@repo/ui'
import { observer } from 'mobx-react-lite'
import Balancer from 'react-wrap-balancer'

import { AppHeader } from '@/components/AppHeader'
import { BetaBadge } from '@/components/BetaBadge'
import { Button } from '@/components/Button'
import { ExternalLink } from '@/components/ExternalLink'
import { BackButton } from '@/components/icon-buttons/IconButtons'
import { List } from '@/components/List'
import { Main } from '@/components/Main'
import { Page } from '@/components/Page'
import { PageHeading } from '@/components/PageHeading'
import { TipBadge } from '@/components/TipBadge'
import { InvalidIcon } from '@/pages/trivia-lists/InvalidIcon'

import { TRIVIA_KNOWLEDGE_BASE_URL } from '../edit-trivia-list/edit-trivia-list'
import { validateTriviaList } from '../edit-trivia-list/utils/validate-list'

type Props = {
  baseUrl?: string
  isLoading: boolean
  isStartingGame?: string
  onDeleteTriviaList: (listId: string) => void
  onStartGame?: (triviaList: TriviaList) => Promise<void>
  pageTitle?: string
  triviaLists: TriviaList[]
}

export const ManageTriviaLists = observer(
  ({
    baseUrl = '/',
    isLoading,
    isStartingGame,
    onDeleteTriviaList,
    onStartGame,
    pageTitle = 'Manage Trivia Lists',
    triviaLists
  }: Props) => {
    if (isLoading) return <Spinner label='Loading...' />

    const validatedTriviaLists = triviaLists.map(validateTriviaList)

    return (
      <Page title={pageTitle}>
        <AppHeader
          actionsLeft={<BackButton url={`${baseUrl}trivia`} />}
          header={
            <div className='flex items-center gap-6'>
              <PageHeading>{pageTitle}</PageHeading>
              <BetaBadge className='relative top-auto' />
            </div>
          }
          secondaryHeader={
            <Container className='py-6' size='medium'>
              <Button text='Create Trivia List' to={`${baseUrl}trivia/lists/create`} />
            </Container>
          }
        />

        <Main>
          <Container className='flex flex-col gap-12' size='medium'>
            {!validatedTriviaLists.length && (
              <Panel className='text-center'>You haven't created any trivia bingo lists yet</Panel>
            )}

            {Boolean(validatedTriviaLists.length) && (
              <div className='flex flex-col gap-4'>
                <Heading className='mb-0'>Your Trivia Lists</Heading>
                <List>
                  {validatedTriviaLists.map(triviaList => (
                    <List.Item
                      actions={
                        <div className='flex items-center gap-3'>
                          {onStartGame && (
                            <Button
                              disabled={
                                !triviaList.validation.isValid || isStartingGame === triviaList.id
                              }
                              isLoading={isStartingGame === triviaList.id}
                              size='small'
                              text='Start Game'
                              onClick={() => onStartGame(triviaList)}
                            />
                          )}

                          <Button
                            size='small'
                            text='Edit'
                            to={`${baseUrl}trivia/lists/edit/${triviaList.id}`}
                          />

                          <IconButton
                            size='small'
                            title='Delete Playlist'
                            onClick={() => {
                              onDeleteTriviaList(triviaList.id)
                            }}
                          >
                            <TrashIcon />
                          </IconButton>
                        </div>
                      }
                      key={triviaList.id}
                      showActionsOnHover
                    >
                      <div className='flex gap-2'>
                        {triviaList.validation.isValid ? null : (
                          <div className='flex size-6 items-center'>
                            <InvalidIcon className='size-4' />
                          </div>
                        )}

                        <div className='flex flex-col gap-1'>
                          <span>{triviaList.title}</span>

                          {triviaList.validation.isValid ? null : (
                            <Balancer className='text-muted text-xs'>
                              This list requires editing before it can be used to play a game
                            </Balancer>
                          )}
                        </div>
                      </div>
                    </List.Item>
                  ))}
                </List>
              </div>
            )}

            <Panel>
              <TipBadge />
              <Description className='mt-2'>
                <ExternalLink href={TRIVIA_KNOWLEDGE_BASE_URL}>
                  Need a hand? Visit our knowledge base
                </ExternalLink>
              </Description>
            </Panel>
          </Container>
        </Main>
      </Page>
    )
  }
)
